// Room state queries for info elements

import { useCallback } from 'react';
import { useRoomStateSelector } from './useRoomStateSelector';
import { InfoElement, InfoElementType } from '@airhead-games/shared/types';

export function useRoomInfoElements() {
  // --- Room State Access ---
  const infoElements = useRoomStateSelector(
    (state) => state?.infoElements || {}
  );

  // --- Helper Functions for Info Elements ---

  /**
   * Get info elements filtered by various criteria
   * @param options - Filter options
   * @param options.infoIds - Optional array of specific info ID(s) to get
   * @param options.infoType - Optional info type to filter by
   * @param options.elementIds - Optional array of element ID(s) to filter by
   * @returns Array of InfoElements matching the filters
   */
  const getInfoElements = useCallback(
    (
      options: {
        infoIds?: string[];
        infoType?: InfoElementType;
        elementIds?: string[];
      } = {}
    ): InfoElement[] => {
      // If infoIds array is specified, return those elements
      if (options.infoIds) {
        return options.infoIds
          .map((id) => infoElements[id])
          .filter((element): element is InfoElement => element !== undefined);
      }

      // Otherwise filter by type and/or elementIds
      return Object.values(infoElements).filter((element) => {
        if (options.infoType && element.type !== options.infoType) {
          return false;
        }
        if (
          options.elementIds &&
          (!element.elementId ||
            !options.elementIds.includes(element.elementId))
        ) {
          return false;
        }
        return true;
      });
    },
    [infoElements]
  );

  /**
   * Find an info element by its content
   * @param content - The content to search for
   * @param infoType - Optional type to filter by
   * @returns The first InfoElement with matching content or undefined if not found
   */
  const findInfoElementByContent = useCallback(
    (content: string, infoType?: InfoElementType): InfoElement | undefined => {
      return Object.values(infoElements).find((element) => {
        if (infoType && element.type !== infoType) {
          return false;
        }
        return element.content === content;
      });
    },
    [infoElements]
  );

  /**
   * Get the last info element by type (highest infoId / last in order)
   * @param infoType - The info type to filter by
   * @returns The last InfoElement of that type or undefined if none found
   */
  const getLastInfoElement = useCallback(
    (infoType: InfoElementType): InfoElement | undefined => {
      const elements = getInfoElements({ infoType });
      // Elements are added in order, so the last one is the latest
      return elements.length > 0 ? elements[elements.length - 1] : undefined;
    },
    [getInfoElements]
  );

  /**
   * Get the nth info element matching the specified criteria
   * @param n - The position (1-indexed) of the element to retrieve
   * @param options - Filter options (same as getInfoElements)
   * @returns The nth InfoElement matching the criteria or undefined if not found
   */
  const getNthInfoElement = useCallback(
    (
      n: number,
      options: {
        infoIds?: string[];
        infoType?: InfoElementType;
        elementIds?: string[];
      } = {}
    ): InfoElement | undefined => {
      if (n < 1) return undefined;

      const elements = getInfoElements(options).sort((a, b) =>
        a.infoId.localeCompare(b.infoId)
      );

      return elements[n - 1]; // 1-indexed
    },
    [getInfoElements]
  );

  /**
   * Count info elements by type up to a specific infoId
   * @param targetInfoId - The infoId to count up to (inclusive)
   * @returns The position/count of the target element (1-indexed)
   */
  const countInfoElementsByType = useCallback(
    (targetInfoId?: string): number => {
      if (!targetInfoId) return 0;

      const targetElement = infoElements[targetInfoId];
      if (!targetElement) return 0;

      // Get all elements of the same type
      const elementsOfType = getInfoElements({
        infoType: targetElement.type,
      }).sort((a, b) => a.infoId.localeCompare(b.infoId));

      // Find the position of our target element
      const position = elementsOfType.findIndex(
        (element) => element.infoId === targetInfoId
      );

      return position >= 0 ? position + 1 : 0; // 1-indexed
    },
    [infoElements, getInfoElements]
  );

  /**
   * Count the total number of info elements by type
   * @param infoType - The info type to count
   * @returns The total count of elements of that type
   */
  const countInfoElementsOfType = useCallback(
    (infoType: InfoElementType): number => {
      return getInfoElements({ infoType }).length;
    },
    [getInfoElements]
  );

  /**
   * Get all info elements sorted by infoId
   * @returns Array of all InfoElements sorted by infoId
   */
  const getAllInfoElements = useCallback((): InfoElement[] => {
    return Object.values(infoElements).sort((a, b) =>
      a.infoId.localeCompare(b.infoId)
    );
  }, [infoElements]);

  /**
   * Check if an info element exists by infoId
   * @param infoId - The infoId to check
   * @returns True if the element exists, false otherwise
   */
  const hasInfoElement = useCallback(
    (infoId: string): boolean => {
      return infoElements[infoId] !== undefined;
    },
    [infoElements]
  );

  /**
   * Get score maps for multiple prompt IDs
   * @param promptIds - Array of prompt IDs to get score maps for
   * @returns Record mapping prompt IDs to their score maps
   */
  const getScoreMapsForPrompts = useCallback(
    (promptIds: string[]): Record<string, Record<string, number>> => {
      const scoreMaps: Record<string, Record<string, number>> = {};

      promptIds.forEach((promptId: string) => {
        const scoreInfo = getInfoElements({
          elementIds: [promptId],
          infoType: InfoElementType.SCORE_MAP,
        })[0];

        if (
          scoreInfo &&
          typeof scoreInfo.content === 'object' &&
          scoreInfo.content !== null
        ) {
          scoreMaps[promptId] = scoreInfo.content as Record<string, number>;
        }
      });

      return scoreMaps;
    },
    [getInfoElements]
  );

  return {
    // Info element query functions
    getInfoElements,
    findInfoElementByContent,
    getLastInfoElement,
    getNthInfoElement,
    countInfoElementsByType,
    countInfoElementsOfType,
    getAllInfoElements,
    hasInfoElement,
    getScoreMapsForPrompts,
  };
}
